import {
    Breadcrumb,
    Grid,
    Header,
    useAppDisplayModeContext,
    useTheme,
    Caption,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './PageHeader.style';
import VariantPricing from '@modules/product/component/variant-pricing/VariantPricing';
import useVariantPrice from '@modules/product/hook/use-variant-price';
import { Link } from '@remix-run/react';
import { superscriptize } from '~/utils';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import PageDetailHeaderCTA from './page-detail-header-cta/PageDetailHeaderCTA';
import type { ElementType } from 'react';
import { useSize, useScroll } from 'ahooks';
import { useEffect, useRef, useState } from 'react';

interface PageHeaderProps {
    data: {
        Breadcrumbs: ProductPageLoaderData['product']['Breadcrumbs'];
        ID: ProductPageLoaderData['product']['ID'];
        Title: ProductPageLoaderData['product']['Title'];
        Pricing: ProductPageLoaderData['product']['Pricing'];
        Professional: Pick<
            ProductPageLoaderData['product']['Professional'],
            | 'Title'
            | 'Link'
            | 'Country'
            | 'PricingDisabled'
            | 'Website'
            | 'PickupLocations'
        >;
        Website?: ProductPageLoaderData['product']['Website'];
        EcommerceReady?: boolean;
        PrimaryCTA: ProductPageLoaderData['primaryCTA'];
    };
    openEnquiryModal: (changeUrl?: boolean) => void;
}

const PageHeader = ({
    data: {
        Breadcrumbs,
        ID,
        Title,
        Pricing,
        Professional,
        Website,
        PrimaryCTA,
    },
    openEnquiryModal,
}: PageHeaderProps) => {
    const { mobile } = useAppDisplayModeContext();
    const { selectedVariant } = useVariantSelectionState();

    const theme = useTheme();
    const { hasPrice: variantHasPrice } = useVariantPrice(Pricing.PricingType);
    const breadcrumbColor = theme.siteVariables.colors.grey[300];
    const hasPrice = variantHasPrice && !Professional.PricingDisabled;
    const iconAdjustedTitle = superscriptize(Title);

    const userCanViewPrice = selectedVariant && selectedVariant.CanViewPrice;
    const headerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [isSticky, setIsSticky] = useState(false);
    const innerRef = useRef<HTMLDivElement>(null);
    const originalHeaderSize = useRef<number>(0);
    const innerSize = useSize(innerRef);
    const headerSize = useScroll();

    useEffect(() => {
        if (headerRef.current && innerSize?.height) {
            originalHeaderSize.current =
                headerRef.current.getBoundingClientRect().height;
        }
    }, [innerSize?.height]);

    useEffect(() => {
        if (!sentinelRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsSticky(!entry?.isIntersecting);
            },
            {
                threshold: [0.01],
                rootMargin: '0px',
            }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, []);

    const { css } = useStyles({
        innerHeight: innerSize?.height,
        scrollY: headerSize?.top,
        originalHeaderSize: originalHeaderSize.current,
        isSticky,
    });

    const shareItem = (
        <PageDetailHeaderCTA
            website={Website || undefined}
            pageID={ID}
            professionalTitle={Professional.Title}
            primaryCTAButtonLabel={
                !mobile ? PrimaryCTA.primaryCTALabel : undefined
            }
            primaryCTAButtonOnClick={() => {
                openEnquiryModal(false);
            }}
            pageType="Product"
        />
    );

    return (
        <>
            <div
                ref={sentinelRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                }}
            />
            <div ref={headerRef} className={css(S.ProductHeaderContainer)}>
                <div className={css(S.PageHeader)}>
                    <div className={css(S.TopPaneContainer)}>
                        {!isSticky && (
                            <Breadcrumb
                                logo={null}
                                home={{
                                    link: true,
                                    as: Link,
                                    label: 'Home',
                                    to: '/',
                                }}
                                items={Breadcrumbs.map((breadcrumb) => ({
                                    label: breadcrumb.label,
                                    href: breadcrumb.href,
                                    as: breadcrumb.as as ElementType,
                                    variables: {
                                        breadcrumbItemTextColor:
                                            breadcrumbColor,
                                    },
                                }))}
                                data-testid="breadcrumb-menu"
                                variables={{
                                    breadcrumbFontSize:
                                        theme.siteVariables.fontSizes.caption04,
                                }}
                            />
                        )}
                    </div>
                    <div ref={innerRef} className={css(S.TitleContainer)}>
                        <Header
                            as="h1"
                            className={css(S.Header)}
                            dangerouslySetInnerHTML={{
                                __html: iconAdjustedTitle,
                            }}
                        />

                        <Grid
                            columns={mobile ? '1fr' : `${pxToRem(880)} 1fr`}
                            className={css({
                                columnGap: pxToRem(44),
                                width: '100%',
                                alignItems: 'center',
                            })}
                            design={{
                                display: mobile ? 'block' : 'grid',
                            }}
                        >
                            <Caption
                                className={css(S.HeaderProfessionalLink)}
                                variables={{
                                    fontSize: mobile
                                        ? theme.siteVariables.fontSizes
                                              .caption04
                                        : theme.siteVariables.fontSizes
                                              .caption03,
                                }}
                            >
                                {!mobile && 'By '}
                                <Caption
                                    as={Link}
                                    to={Professional.Link}
                                    className={css(S.HeaderProfessionalLink)}
                                    variables={{
                                        fontSize: mobile
                                            ? theme.siteVariables.fontSizes
                                                  .caption04
                                            : theme.siteVariables.fontSizes
                                                  .caption03,
                                    }}
                                >
                                    {mobile && 'By '}
                                    {Professional.Title}
                                </Caption>
                            </Caption>

                            {!mobile && (
                                <div className={css(S.HeaderCTA)}>
                                    <div>
                                        {hasPrice && userCanViewPrice && (
                                            <VariantPricing
                                                pricingType={
                                                    Pricing.PricingType
                                                }
                                            />
                                        )}
                                    </div>
                                    <div>{shareItem}</div>
                                </div>
                            )}
                        </Grid>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PageHeader;
